import { BACKEND_URL } from "../env";
import { ApiError } from "../types/api-error";
import { RegistrationRequest } from "../types/registration";

/**
 * NestJS sends DTO validation failures as `message: string[]` without a code;
 * the registration service's own errors carry a `code` the page localizes.
 */
async function readApiError(response: Response) {
  const body = await response.json().catch(() => ({}));
  const validationFailed = Array.isArray(body?.message);
  const code: string | undefined =
    body?.code ?? (validationFailed ? "validation_failed" : undefined);
  const message: string | undefined = validationFailed
    ? body.message[0]
    : body?.message;
  return { code, message };
}

export async function checkEmailAvailability(
  email: string
): Promise<{ available: boolean; error?: ApiError; message?: string }> {
  try {
    const response = await fetch(
      `${BACKEND_URL}/registration/check-email?email=${encodeURIComponent(email)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (response.status === 409) {
      const errorData = await response.json().catch(() => ({}));
      const code = errorData?.code ?? "conflict";
      return {
        available: false,
        error: {
          status: 409,
          code,
          message: errorData.message || "User with this email already exists",
        },
        message: errorData.message || "User with this email already exists",
      };
    }

    // The backend applies the registration DTO's email rules here.
    if (response.status === 400) {
      const { code, message = "Please provide a valid email address" } =
        await readApiError(response);
      return code
        ? { available: false, error: { status: 400, code, message }, message }
        : { available: false, message };
    }

    if (response.ok) {
      return {
        available: true,
      };
    }

    throw new Error("Failed to check email availability");
  } catch (error) {
    console.error("Email check error:", error);
    return {
      available: false,
      message:
        error instanceof Error
          ? error.message
          : "Failed to check email availability",
    };
  }
}

export async function checkAccountNameAvailability(
  accountName: string
): Promise<{ available: boolean; error?: ApiError; message?: string }> {
  try {
    const response = await fetch(
      `${BACKEND_URL}/registration/check-account-name?accountName=${encodeURIComponent(accountName)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (response.status === 409) {
      const errorData = await response.json().catch(() => ({}));
      const code = errorData?.code ?? "conflict";
      return {
        available: false,
        error: {
          status: 409,
          code,
          message:
            errorData.message || `Account "${accountName}" already exists`,
        },
        message: errorData.message || `Account "${accountName}" already exists`,
      };
    }

    // The backend rejects names whose slug would be empty or too long.
    if (response.status === 400) {
      const { code, message = "Invalid account name" } =
        await readApiError(response);
      return code
        ? { available: false, error: { status: 400, code, message }, message }
        : { available: false, message };
    }

    if (response.ok) {
      return {
        available: true,
      };
    }

    throw new Error("Failed to check account name availability");
  } catch (error) {
    console.error("Account name check error:", error);
    return {
      available: false,
      message:
        error instanceof Error
          ? error.message
          : "Failed to check account name availability",
    };
  }
}

export async function requestPasswordReset(
  email: string
): Promise<{ success: boolean }> {
  try {
    await fetch(`${BACKEND_URL}/registration/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    return { success: true };
  } catch {
    return { success: true };
  }
}

export async function submitRegistration(
  data: RegistrationRequest
): Promise<{ success: boolean; message?: string; code?: string }> {
  try {
    const requestData = {
      email: data.email,
      companyName: data.companyName,
      accountName: data.accountName,
      firstName: data.firstName,
      lastName: data.lastName,
      password: data.password,
      confirmPassword: data.confirmPassword,
      language: data.language,
    };

    // Use the backend endpoint instead of the external webhook
    const response = await fetch(`${BACKEND_URL}/registration`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestData),
    });

    if (response.status === 409 || response.status === 400) {
      const { code, message } = await readApiError(response);
      const fallback =
        response.status === 409
          ? "User with this email already exists"
          : "Registration failed with status 400.";
      return code
        ? { success: false, message: message || fallback, code }
        : { success: false, message: message || fallback };
    }

    if (response.status >= 400) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Registration failed with status ${response.status}.`
      );
    }

    if (response.ok) {
      const result = await response.json();
      return {
        success: true,
        message: result.message || "Registration submitted successfully!",
      };
    } else {
      throw new Error("Registration failed");
    }
  } catch (error) {
    console.error("Registration error:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Registration failed",
    };
  }
}
